import {
  ARROW_HEADS,
  FONT_SIZES,
  SHAPE_FILLS,
  STICKY_COLORS,
  STROKE_COLORS,
  notebookPaper,
  NOTEBOOK_PAGE_SIZE,
  type ArrowHead,
  type BoardNode,
  type ConnectorRouting,
  type DrawNode,
  type FontFamily,
  type FrameNode,
  type NotebookNode,
  type NotebookPaper,
  type StrokeStyle,
  type TextAlign,
  type VerticalAlign,
} from '@kanvas/board-core';
import {
  alignSelection,
  applyShapeStyle,
  applyTextStyle,
  copyStyleToSelection,
  deleteSelection,
  distributeSelection,
  duplicateSelection,
  explodeSticky,
  groupSelection,
  autoLayoutMindmap,
  presentationFrames,
  reorderSelection,
  setFramePresentationOrder,
  setHidden,
  setLocked,
  setOpacity,
  setStickyFill,
  setTags,
  startPresentation,
  tidySelection,
  ungroupSelection,
  type ActionContext,
} from '../actions/actions';
import {
  applyImageCrop,
  beginImageCrop,
  cancelImageCrop,
  resetImageCrop,
} from '../interaction/gestures';
import { useEffect, useMemo, useState } from 'react';
import { useDocVersion } from '../collab/hooks';
import { useEditor } from '../state/store';
import {
  ColorGrid,
  IconButton,
  PanelDivider,
  PanelGroup,
  Popover,
  Segmented,
  Slider,
  Stepper,
  Swatch,
} from './controls';
import { Icon } from './icons';

export interface StylePanelProps {
  ctx: ActionContext;
}

/**
 * Contextual style bar.
 *
 * Shown for the current selection, or for the active creation tool when nothing
 * is selected — so setting a colour before drawing works the same as changing
 * it afterwards. Every control writes through the action layer, which keeps the
 * "apply to selection AND remember as the default" behaviour in one place.
 */
export function StylePanel({ ctx }: StylePanelProps) {
  const selection = useEditor((s) => s.selection);
  const tool = useEditor((s) => s.tool);
  const editingId = useEditor((s) => s.editingId);
  // Re-read on every document change so the panel reflects live values.
  const version = useDocVersion(ctx.doc);
  const nodes = useMemo(() => ctx.doc.getMany(selection), [ctx.doc, selection, version]);

  const hasSelection = nodes.length > 0;
  const kinds = new Set(nodes.map((n) => n.type));

  const showShape = hasSelection ? kinds.has('shape') : tool === 'shape';
  const showSticky = hasSelection ? kinds.has('sticky') : tool === 'sticky';
  const showText =
    hasSelection
      ? kinds.has('text') || kinds.has('shape') || kinds.has('sticky') || kinds.has('connector')
      : tool === 'text' || tool === 'shape' || tool === 'sticky';
  const showDraw = hasSelection ? kinds.has('draw') : tool === 'draw';
  const showConnector = hasSelection ? kinds.has('connector') : tool === 'connector';
  const showFrame = hasSelection && kinds.has('frame') && kinds.size === 1 && nodes.length === 1;
  const showImage = hasSelection && kinds.has('image') && kinds.size === 1 && nodes.length === 1;
  const showNotebook =
    hasSelection && kinds.has('notebook') && kinds.size === 1 && nodes.length === 1;
  const croppingId = useEditor((s) => s.croppingId);

  if (!hasSelection && (tool === 'select' || tool === 'hand' || tool === 'comment' || tool === 'laser')) {
    return null;
  }
  if (editingId !== null && nodes.length <= 1 && !hasSelection) return null;

  return (
    <div className="style-panel" role="toolbar" aria-label="Стиль">
      {showSticky && <StickySection ctx={ctx} nodes={nodes} />}
      {showShape && <ShapeSection ctx={ctx} nodes={nodes} />}
      {showConnector && <ConnectorSection ctx={ctx} nodes={nodes} />}
      {showDraw && <DrawSection ctx={ctx} nodes={nodes} />}
      {showFrame && <FrameSection ctx={ctx} nodes={nodes} />}
      {showImage && <ImageSection ctx={ctx} nodes={nodes} cropping={croppingId === nodes[0]?.id} />}
      {showNotebook && <NotebookSection ctx={ctx} nodes={nodes} />}
      {showText && <TextSection ctx={ctx} nodes={nodes} />}

      {hasSelection && (
        <>
          <PanelDivider />
          <ArrangeSection ctx={ctx} nodes={nodes} />
        </>
      )}
    </div>
  );
}

interface SectionProps {
  ctx: ActionContext;
  nodes: BoardNode[];
}

function StickySection({ ctx, nodes }: SectionProps) {
  const fallback = useEditor((s) => s.stickyFill);
  const sticky = nodes.find((n) => n.type === 'sticky');
  const value = sticky?.type === 'sticky' ? sticky.fill : fallback;

  return (
    <PanelGroup>
      <Popover
        title="Цвет стикера"
        trigger={() => (
          <>
            <Swatch color={value} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {(close) => (
          <ColorGrid
            colors={STICKY_COLORS}
            value={value}
            onPick={(color) => {
              setStickyFill(ctx, color);
              close();
            }}
          />
        )}
      </Popover>
      {nodes.some((n) => n.type === 'sticky' && n.text.includes('\n')) && (
        <IconButton
          icon="ungroup"
          label="Разбить каждую строку на отдельный стикер"
          onClick={() => explodeSticky(ctx)}
        />
      )}
    </PanelGroup>
  );
}

function ShapeSection({ ctx, nodes }: SectionProps) {
  const fallback = useEditor((s) => s.shapeStyle);
  const shape = nodes.find((n) => n.type === 'shape');
  const style = shape?.type === 'shape' ? shape.style : fallback;

  return (
    <PanelGroup>
      <Popover
        title="Заливка"
        trigger={() => (
          <>
            <Swatch color={style.fill} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {(close) => (
          <ColorGrid
            colors={SHAPE_FILLS}
            value={style.fill}
            onPick={(fill) => {
              applyShapeStyle(ctx, {
                fill,
                fillStyle: fill === 'transparent' ? 'none' : 'solid',
              });
              close();
            }}
          />
        )}
      </Popover>

      <Popover
        title="Контур"
        trigger={() => (
          <>
            <span className="stroke-preview" style={{ borderColor: style.stroke }} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {() => (
          <div className="popover-stack">
            <ColorGrid
              colors={STROKE_COLORS}
              value={style.stroke}
              onPick={(stroke) => applyShapeStyle(ctx, { stroke })}
            />
            <PanelDivider />
            <Segmented<StrokeStyle>
              value={style.strokeStyle}
              options={[
                { value: 'solid', label: 'Сплошная' },
                { value: 'dashed', label: 'Штрихи' },
                { value: 'dotted', label: 'Точки' },
              ]}
              onChange={(strokeStyle) => applyShapeStyle(ctx, { strokeStyle })}
            />
            <Stepper
              value={style.strokeWidth}
              min={0}
              max={24}
              step={1}
              suffix="px"
              onChange={(strokeWidth) => applyShapeStyle(ctx, { strokeWidth })}
            />
            <label className="checkbox">
              <input
                type="checkbox"
                checked={style.shadow}
                onChange={(e) => applyShapeStyle(ctx, { shadow: e.target.checked })}
              />
              Тень
            </label>
          </div>
        )}
      </Popover>
    </PanelGroup>
  );
}

function ConnectorSection({ ctx, nodes }: SectionProps) {
  const fallback = useEditor((s) => s.connectorStyle);
  const connector = nodes.find((n) => n.type === 'connector');
  const style =
    connector?.type === 'connector'
      ? {
          routing: connector.routing,
          startHead: connector.startHead,
          endHead: connector.endHead,
          stroke: connector.stroke,
          strokeWidth: connector.strokeWidth,
          strokeStyle: connector.strokeStyle,
        }
      : fallback;

  const patch = (next: Partial<typeof style>) => {
    useEditor.getState().patchConnectorStyle(next);
    const ids = nodes.filter((n) => n.type === 'connector').map((n) => n.id);
    if (ids.length === 0) return;
    const patches = new Map(ids.map((id) => [id, next as Partial<BoardNode>]));
    ctx.doc.updateMany(patches);
  };

  return (
    <PanelGroup>
      <Segmented<ConnectorRouting>
        value={style.routing}
        options={[
          { value: 'straight', label: 'Прямая' },
          { value: 'elbow', label: 'Ломаная' },
          { value: 'curved', label: 'Кривая' },
        ]}
        onChange={(routing) => patch({ routing })}
      />
      <Popover
        title="Концы линии"
        trigger={() => (
          <>
            <ArrowHeadGlyph head={style.endHead} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {() => (
          <div className="popover-stack">
            <div className="popover-label">Начало</div>
            <div className="head-grid">
              {ARROW_HEADS.map((head) => (
                <button
                  key={`s-${head}`}
                  type="button"
                  className={head === style.startHead ? 'is-active' : ''}
                  onClick={() => patch({ startHead: head })}
                  title={arrowHeadLabel(head)}
                >
                  <ArrowHeadGlyph head={head} flip />
                </button>
              ))}
            </div>
            <div className="popover-label">Конец</div>
            <div className="head-grid">
              {ARROW_HEADS.map((head) => (
                <button
                  key={`e-${head}`}
                  type="button"
                  className={head === style.endHead ? 'is-active' : ''}
                  onClick={() => patch({ endHead: head })}
                  title={arrowHeadLabel(head)}
                >
                  <ArrowHeadGlyph head={head} />
                </button>
              ))}
            </div>
          </div>
        )}
      </Popover>
      <Popover
        title="Стиль линии"
        trigger={() => (
          <>
            <span className="stroke-preview" style={{ borderColor: style.stroke }} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {() => (
          <div className="popover-stack">
            <ColorGrid
              colors={STROKE_COLORS}
              value={style.stroke}
              onPick={(stroke) => patch({ stroke })}
            />
            <PanelDivider />
            <Segmented<StrokeStyle>
              value={style.strokeStyle}
              options={[
                { value: 'solid', label: 'Сплошная' },
                { value: 'dashed', label: 'Штрихи' },
                { value: 'dotted', label: 'Точки' },
              ]}
              onChange={(strokeStyle) => patch({ strokeStyle })}
            />
            <Stepper
              value={style.strokeWidth}
              min={1}
              max={16}
              suffix="px"
              onChange={(strokeWidth) => patch({ strokeWidth })}
            />
          </div>
        )}
      </Popover>
    </PanelGroup>
  );
}

function DrawSection({ ctx, nodes }: SectionProps) {
  const fallback = useEditor((s) => s.drawStyle);
  const shapeAssistEnabled = useEditor((s) => s.shapeAssistEnabled);
  const drawNodes = nodes.filter((n): n is DrawNode => n.type === 'draw');
  // Like every other section: show the selected object's own values, and the
  // tool defaults only when nothing is selected. The panel used to show the
  // defaults over a selected stroke, so a red stroke read as black.
  const first = drawNodes[0];
  const drawStyle = first
    ? { color: first.color, size: first.size, brush: first.brush }
    : fallback;

  const patch = (next: Partial<typeof drawStyle>) => {
    useEditor.getState().patchDrawStyle(next);
    if (drawNodes.length === 0) return;
    const patches = new Map(
      drawNodes.map((n) => [n.id, next as unknown as Partial<BoardNode>]),
    );
    ctx.doc.updateMany(patches);
  };

  return (
    <PanelGroup>
      <Popover
        title="Цвет"
        trigger={() => (
          <>
            <Swatch color={drawStyle.color} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {(close) => (
          <ColorGrid
            colors={STROKE_COLORS}
            value={drawStyle.color}
            onPick={(color) => {
              patch({ color });
              close();
            }}
          />
        )}
      </Popover>
      <Segmented
        label="Кисть"
        value={drawStyle.brush}
        options={[
          { value: 'pen', label: 'Перо', icon: 'pen' },
          { value: 'marker', label: 'Маркер', icon: 'marker' },
          { value: 'highlighter', label: 'Выделитель', icon: 'highlighter' },
        ]}
        onChange={(brush) => patch({ brush })}
      />
      <Stepper
        value={drawStyle.size}
        min={1}
        max={64}
        suffix="px"
        onChange={(size) => patch({ size })}
      />
      <IconButton
        icon="magic"
        label="Магическая ручка: рисуй квадрат или круг — они станут идеальными"
        active={shapeAssistEnabled}
        onClick={() =>
          useEditor.getState().setUi({ shapeAssistEnabled: !shapeAssistEnabled })
        }
      />
    </PanelGroup>
  );
}

function FrameSection({ ctx, nodes }: SectionProps) {
  const version = useDocVersion(ctx.doc);
  const frame = nodes.find((n): n is FrameNode => n.type === 'frame');
  const ordered = useMemo(
    () => presentationFrames(ctx.doc).filter((f) => f.slideIndex !== null),
    [ctx.doc, version],
  );
  if (!frame) return null;
  const position = frame.slideIndex !== null ? ordered.findIndex((f) => f.id === frame.id) : -1;
  const inDeck = frame.slideIndex !== null;

  return (
    <PanelGroup>
      {inDeck ? (
        <>
          <span className="popover-label" style={{ padding: '0 4px' }}>
            Слайд {position + 1} / {ordered.length}
          </span>
          <button
            type="button"
            className="icon-btn"
            disabled={position <= 0}
            aria-label="Переместить раньше в презентации"
            data-tip="Переместить раньше"
            onClick={() => setFramePresentationOrder(ctx.doc, frame.id, 'up')}
          >
            <Icon name="arrowLeft" size={16} />
          </button>
          <button
            type="button"
            className="icon-btn"
            disabled={position >= ordered.length - 1}
            aria-label="Переместить позже в презентации"
            data-tip="Переместить позже"
            onClick={() => setFramePresentationOrder(ctx.doc, frame.id, 'down')}
          >
            <Icon name="arrowLeft" size={16} className="icon-flip" />
          </button>
          <IconButton
            icon="close"
            label="Убрать из презентации"
            onClick={() => setFramePresentationOrder(ctx.doc, frame.id, 'remove')}
          />
        </>
      ) : (
        <button
          type="button"
          className="menu-item"
          onClick={() => setFramePresentationOrder(ctx.doc, frame.id, 'add')}
        >
          <Icon name="template" size={16} /> Добавить в презентацию
        </button>
      )}
      <PanelDivider />
      <IconButton
        icon="play"
        label="Показать презентацию с этого фрейма"
        onClick={() => startPresentation(ctx.doc, frame.id)}
      />
    </PanelGroup>
  );
}

function NotebookSection({ ctx, nodes }: SectionProps) {
  const notebook = nodes.find((n): n is NotebookNode => n.type === 'notebook');
  if (!notebook) return null;
  const paper = notebookPaper(notebook);

  return (
    <PanelGroup>
      <span className="popover-label" style={{ padding: '0 4px' }}>
        Страница {NOTEBOOK_PAGE_SIZE.w}×{NOTEBOOK_PAGE_SIZE.h}
      </span>
      <Segmented<NotebookPaper>
        value={paper}
        options={[
          { value: 'grid', label: 'Клетка' },
          { value: 'lined', label: 'Линейка' },
          { value: 'blank', label: 'Чистая' },
        ]}
        onChange={(next) => {
          ctx.doc.update<NotebookNode>(notebook.id, { paper: next });
          ctx.doc.breakUndoBatch();
        }}
      />
    </PanelGroup>
  );
}

function ImageSection({
  ctx,
  nodes,
  cropping,
}: SectionProps & { cropping: boolean }) {
  const image = nodes.find((n) => n.type === 'image');
  if (!image || image.type !== 'image') return null;

  return (
    <PanelGroup>
      {cropping ? (
        <>
          <span className="popover-label" style={{ padding: '0 4px', maxWidth: 160 }}>
            Нарисуйте область или тяните рамку
          </span>
          <IconButton
            icon="check"
            label="Применить обрезку · Enter"
            onClick={() => applyImageCrop(ctx)}
          />
          <IconButton
            icon="close"
            label="Отменить обрезку · Esc"
            onClick={() => cancelImageCrop()}
          />
        </>
      ) : (
        <>
          <IconButton
            icon="crop"
            label="Обрезать изображение"
            onClick={() => {
              useEditor.getState().select([image.id]);
              beginImageCrop(image.id);
            }}
          />
          {(image.crop || (image.mask && image.mask.length >= 3)) && (
            <IconButton
              icon="refresh"
              label="Сбросить обрезку"
              onClick={() => resetImageCrop(ctx, image.id)}
            />
          )}
        </>
      )}
    </PanelGroup>
  );
}

function TextSection({ ctx, nodes }: SectionProps) {
  const fallback = useEditor((s) => s.textStyle);
  const withText = nodes.find(
    (n) => n.type === 'text' || n.type === 'shape' || n.type === 'sticky' || n.type === 'connector',
  );
  const style =
    withText && 'textStyle' in withText ? withText.textStyle : fallback;

  return (
    <PanelGroup>
      <Popover
        title="Шрифт"
        align="left"
        trigger={() => (
          <>
            <span className="font-label" style={{ fontFamily: fontPreview(style.fontFamily) }}>
              Aa
            </span>
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {() => (
          <div className="popover-stack">
            <Segmented<FontFamily>
              value={style.fontFamily}
              options={[
                { value: 'sans', label: 'Без засечек' },
                { value: 'serif', label: 'С засечками' },
                { value: 'mono', label: 'Моно' },
                { value: 'marker', label: 'Маркер' },
              ]}
              onChange={(fontFamily) => applyTextStyle(ctx, { fontFamily })}
            />
            <div className="size-grid">
              {FONT_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={size === style.fontSize ? 'is-active' : ''}
                  onClick={() => applyTextStyle(ctx, { fontSize: size })}
                >
                  {size}
                </button>
              ))}
            </div>
            <PanelDivider />
            <ColorGrid
              colors={STROKE_COLORS}
              value={style.color}
              onPick={(color) => applyTextStyle(ctx, { color })}
            />
          </div>
        )}
      </Popover>

      <Stepper
        value={style.fontSize}
        min={6}
        max={288}
        onChange={(fontSize) => applyTextStyle(ctx, { fontSize })}
      />

      <IconButton
        icon="bold"
        label="Полужирный"
        active={style.bold}
        onClick={() => applyTextStyle(ctx, { bold: !style.bold })}
      />
      <IconButton
        icon="italic"
        label="Курсив"
        active={style.italic}
        onClick={() => applyTextStyle(ctx, { italic: !style.italic })}
      />
      <IconButton
        icon="underline"
        label="Подчёркнутый"
        active={style.underline}
        onClick={() => applyTextStyle(ctx, { underline: !style.underline })}
      />
      <IconButton
        icon="strike"
        label="Зачёркнутый"
        active={style.strike}
        onClick={() => applyTextStyle(ctx, { strike: !style.strike })}
      />

      <Popover
        title="Выравнивание"
        trigger={() => (
          <>
            <Icon name={alignIcon(style.align)} size={16} />
            <Icon name="chevronDown" size={12} />
          </>
        )}
      >
        {() => (
          <div className="popover-stack">
            <Segmented<TextAlign>
              value={style.align}
              options={[
                { value: 'left', label: 'Слева', icon: 'alignLeft' },
                { value: 'center', label: 'По центру', icon: 'alignCenterH' },
                { value: 'right', label: 'Справа', icon: 'alignRight' },
              ]}
              onChange={(align) => applyTextStyle(ctx, { align })}
            />
            <Segmented<VerticalAlign>
              value={style.valign}
              options={[
                { value: 'top', label: 'Сверху', icon: 'alignTop' },
                { value: 'middle', label: 'По центру', icon: 'alignCenterV' },
                { value: 'bottom', label: 'Снизу', icon: 'alignBottom' },
              ]}
              onChange={(valign) => applyTextStyle(ctx, { valign })}
            />
          </div>
        )}
      </Popover>
    </PanelGroup>
  );
}

function ArrangeSection({ ctx, nodes }: SectionProps) {
  const multi = nodes.length > 1;
  const anyLocked = nodes.some((n) => n.locked);
  const opacity = nodes[0]?.opacity ?? 1;

  return (
    <PanelGroup>
      {multi && (
        <Popover
          title="Выровнять"
          trigger={() => <Icon name="alignLeft" size={16} />}
        >
          {() => (
            <div className="popover-stack">
              <div className="icon-row">
                <IconButton icon="alignLeft" label="Выровнять по левому краю" onClick={() => alignSelection(ctx, 'left')} />
                <IconButton icon="alignCenterH" label="Выровнять по центру" onClick={() => alignSelection(ctx, 'center-h')} />
                <IconButton icon="alignRight" label="Выровнять по правому краю" onClick={() => alignSelection(ctx, 'right')} />
              </div>
              <div className="icon-row">
                <IconButton icon="alignTop" label="Выровнять по верхнему краю" onClick={() => alignSelection(ctx, 'top')} />
                <IconButton icon="alignCenterV" label="Выровнять по середине" onClick={() => alignSelection(ctx, 'center-v')} />
                <IconButton icon="alignBottom" label="Выровнять по нижнему краю" onClick={() => alignSelection(ctx, 'bottom')} />
              </div>
              <PanelDivider />
              <div className="icon-row">
                <IconButton
                  icon="distributeH"
                  label="Распределить по горизонтали"
                  disabled={nodes.length < 3}
                  onClick={() => distributeSelection(ctx, 'x')}
                />
                <IconButton
                  icon="distributeV"
                  label="Распределить по вертикали"
                  disabled={nodes.length < 3}
                  onClick={() => distributeSelection(ctx, 'y')}
                />
                <IconButton icon="grid" label="Упорядочить в сетку" onClick={() => tidySelection(ctx)} />
              </div>
            </div>
          )}
        </Popover>
      )}

      <Popover title="Слой" trigger={() => <Icon name="toFront" size={16} />}>
        {() => (
          <div className="popover-stack">
            <button type="button" className="menu-item" onClick={() => reorderSelection(ctx, 'front')}>
              <Icon name="toFront" size={16} /> На передний план
            </button>
            <button type="button" className="menu-item" onClick={() => reorderSelection(ctx, 'forward')}>
              <Icon name="toFront" size={16} /> Переместить вперёд
            </button>
            <button type="button" className="menu-item" onClick={() => reorderSelection(ctx, 'backward')}>
              <Icon name="toBack" size={16} /> Переместить назад
            </button>
            <button type="button" className="menu-item" onClick={() => reorderSelection(ctx, 'back')}>
              <Icon name="toBack" size={16} /> На задний план
            </button>
          </div>
        )}
      </Popover>

      <Popover title="Ещё" trigger={() => <Icon name="more" size={16} />}>
        {() => (
          <div className="popover-stack">
            <Slider
              label="Прозрачность"
              value={Math.round(opacity * 100)}
              min={10}
              max={100}
              onChange={(v) => setOpacity(ctx, v / 100)}
            />
            <PanelDivider />
            {multi && (
              <button type="button" className="menu-item" onClick={() => groupSelection(ctx)}>
                <Icon name="group" size={16} /> Сгруппировать <kbd>Ctrl G</kbd>
              </button>
            )}
            {nodes.some((n) => n.type === 'group') && (
              <button type="button" className="menu-item" onClick={() => ungroupSelection(ctx)}>
                <Icon name="ungroup" size={16} /> Разгруппировать
              </button>
            )}
            {multi && (
              <button type="button" className="menu-item" onClick={() => copyStyleToSelection(ctx)}>
                <Icon name="copy" size={16} /> Применить стиль первого элемента
              </button>
            )}
            <button type="button" className="menu-item" onClick={() => setLocked(ctx, !anyLocked)}>
              <Icon name={anyLocked ? 'unlock' : 'lock'} size={16} />
              {anyLocked ? 'Разблокировать' : 'Заблокировать'}
            </button>
            <button
              type="button"
              className="menu-item"
              onClick={() => setHidden(ctx.doc, nodes.map((n) => n.id), true)}
            >
              <Icon name="layers" size={16} /> Скрыть
            </button>
            {!multi && nodes[0]?.type !== 'connector' && nodes[0]?.type !== 'group' && (
              <button type="button" className="menu-item" onClick={() => autoLayoutMindmap(ctx)}>
                <Icon name="sitemap" size={16} /> Авто-раскладка mind map
              </button>
            )}
            <PanelDivider />
            <TagEditor ctx={ctx} nodes={nodes} />
          </div>
        )}
      </Popover>

      <IconButton icon="copy" label="Дублировать" onClick={() => duplicateSelection(ctx)} />
      <IconButton icon="trash" label="Удалить" onClick={() => deleteSelection(ctx)} />
    </PanelGroup>
  );
}

function TagEditor({ ctx, nodes }: SectionProps) {
  const initial = nodes[0]?.tags?.join(', ') ?? '';
  const [value, setValue] = useState(initial);

  useEffect(() => setValue(initial), [initial]);

  const commit = () => {
    const tags = value
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');
    setTags(ctx, tags);
  };

  return (
    <div className="tag-editor">
      <div className="popover-label">Теги</div>
      <input
        type="text"
        className="tag-input"
        placeholder="дизайн, срочно, вопрос…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
    </div>
  );
}

function ArrowHeadGlyph({ head, flip }: { head: ArrowHead; flip?: boolean }) {
  return (
    <svg width={28} height={14} viewBox="0 0 28 14" aria-hidden="true">
      <g transform={flip ? 'scale(-1,1) translate(-28,0)' : undefined}>
        <line x1={2} y1={7} x2={20} y2={7} stroke="currentColor" strokeWidth={1.6} />
        {head === 'arrow' && (
          <path d="M18 3l5 4-5 4" fill="none" stroke="currentColor" strokeWidth={1.6} />
        )}
        {head === 'triangle' && (
          <path d="M18 3l6 4-6 4z" fill="none" stroke="currentColor" strokeWidth={1.6} />
        )}
        {head === 'filledTriangle' && <path d="M18 3l6 4-6 4z" fill="currentColor" />}
        {head === 'circle' && (
          <circle cx={21} cy={7} r={3} fill="none" stroke="currentColor" strokeWidth={1.6} />
        )}
        {head === 'filledCircle' && <circle cx={21} cy={7} r={3} fill="currentColor" />}
        {head === 'diamond' && <path d="M18 7l3-3 3 3-3 3z" fill="currentColor" />}
        {head === 'bar' && (
          <line x1={21} y1={2} x2={21} y2={12} stroke="currentColor" strokeWidth={1.8} />
        )}
      </g>
    </svg>
  );
}

const ARROW_HEAD_LABELS: Record<ArrowHead, string> = {
  none: 'Нет',
  arrow: 'Стрелка',
  triangle: 'Треугольник',
  filledTriangle: 'Заполненный треугольник',
  circle: 'Круг',
  filledCircle: 'Заполненный круг',
  diamond: 'Ромб',
  bar: 'Черта',
};

function arrowHeadLabel(head: ArrowHead): string {
  return ARROW_HEAD_LABELS[head] ?? head;
}

function alignIcon(align: TextAlign) {
  return align === 'left' ? 'alignLeft' : align === 'right' ? 'alignRight' : 'alignCenterH';
}

function fontPreview(family: FontFamily): string {
  switch (family) {
    case 'serif':
      return 'Source Serif 4, Georgia, serif';
    case 'mono':
      return 'JetBrains Mono, monospace';
    case 'marker':
      return 'Permanent Marker, cursive';
    default:
      return 'Inter, system-ui, sans-serif';
  }
}
